import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { stateAt } from '../city/cityMotion';
import { serverNow, cityEngage } from '../services/worldSocket';
import { seatPose } from '../services/seats';
import { cafeArtists } from '../state/cafeArtists';
import AnimatedResident from './AnimatedResident';
import SafeMount from './SafeMount';
import WorldLabel from './WorldLabel';
import { bodyScene } from './SceneChat';

// CafeGuests — an artist sitting at a café table (2026-10-31).
//
// Khari: "Each table in the Jahva house has 2 chairs. This would be a great
// place to be able to chat with another user/artist who is sitting there, and
// not have your conversation broadcasted/audible to others" — and artists may
// sit at tables too. The CITY seats them (API CafeGuests: their own yes to a
// stretch at a table): a stay in this room whose spot is a chair (`cf`). Here
// they are drawn in that chair, sitting (the procedural `sitting` pose, no
// clip), facing their table, with their name small over them.
//
// Their chair is not offered to anyone while they are in it (CafeCounter reads
// `cafeArtists`; the server refuses it too). Sit down ACROSS from them and the
// conversation opens — with them, the same one-to-one chat as anywhere in the
// city, and while they are at a table their side of it is not echoed to anyone
// else (API sockets/world city_reply). A tap on them opens it as well.

const CHECK_MS = 1000;

function Guest({ g, model, toLocal, onTap, anchorRef }) {
  const npcList = useWorldStore((s) => s.npcList);
  const body = useMemo(() => (npcList || []).find((n) => Number(n?.avatarId) === Number(g.a) && n.rigged && !n.test) || null, [npcList, g.a]);
  const at = useMemo(() => {
    const unit = model?.getObjectByName(g.seat);
    const pose = unit ? seatPose(unit) : null;
    if (!pose) return null;
    const p = toLocal(new THREE.Vector3(pose.pos[0], 0, pose.pos[2]));
    return { pos: [p.x, 0, p.z], yaw: pose.yaw };
  }, [model, g.seat, toLocal]);
  if (!at) return null;
  const height = body?.height || 0.75;
  return (
    <group position={at.pos} rotation={[0, at.yaw, 0]} ref={anchorRef}>
      {body ? (
        <SafeMount label={`cafe guest ${g.name}`} retryMs={30000} onRetry={() => useGLTF.clear([body.rigged].filter(Boolean))}>
          <Suspense fallback={null}>
            <AnimatedResident
              meshUrl={body.rigged}
              rigPose={body.pose || null}
              clipMap={body.clips || null}
              pose="sitting"
              avatarId={g.a}
              position={[0, 0, 0]}
              rotationY={0}
              height={height}
              indoor
              lightScale={0.45}
              onSelect={onTap}
            />
          </Suspense>
        </SafeMount>
      ) : (
        <mesh position={[0, height * 0.32, 0]} onClick={(e) => { e.stopPropagation(); onTap(); }}>
          <capsuleGeometry args={[height * 0.13, height * 0.3, 4, 8]} />
          <meshStandardMaterial color="#2e2a3a" roughness={0.8} />
        </mesh>
      )}
      <WorldLabel position={[0, height * 0.86, 0]} center occlude={false} style={{ pointerEvents: 'none' }}>
        <div style={{ background: 'rgba(14,9,6,0.82)', color: '#ffe9c4', font: '600 11px system-ui', padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
          {g.name.split(' ')[0]}
        </div>
      </WorldLabel>
    </group>
  );
}

export default function CafeGuests({ roomId, model }) {
  const room = ROOMS[roomId];
  const tables = room?.tables || [];
  const openChat = useWorldStore((s) => s.openChat);
  const grp = useRef(null);
  const anchors = useRef(new Map());   // seat → the group a guest sits in (their chat scene follows it)
  const [guests, setGuests] = useState([]);
  const opened = useRef(null);

  // Who the city has sitting in this room's chairs, once a second.
  useEffect(() => {
    const check = () => {
      const cs = useCityStore.getState();
      const t = serverNow();
      const out = [];
      for (const r of Object.values(cs.byId || {})) {
        const st = stateAt(r, t, cs.convs);
        if (st?.inside === roomId && st.seg?.cf && typeof st.spot === 'string') out.push({ a: Number(r.avatarId), name: r.name || '', seat: st.spot, table: String(st.seg.cf) });
      }
      cafeArtists.bySeat = new Map(out.map((g) => [g.seat, g]));
      setGuests((cur) => (cur.map((g) => `${g.a}:${g.seat}`).join() === out.map((g) => `${g.a}:${g.seat}`).join() ? cur : out));
    };
    check();
    const iv = setInterval(check, CHECK_MS);
    return () => { clearInterval(iv); cafeArtists.bySeat = new Map(); };
  }, [roomId]);

  // Their no, said where you are looking (the chat log above the player bar),
  // never a tap that does nothing — that read as the table being broken.
  const talkTo = async (g) => {
    const r = await cityEngage(g.a);
    if (!r?.ok) {
      const first = g.name.split(' ')[0] || 'They';
      const why = r?.reason === 'talking' ? `${first} is talking with someone right now.`
        : r?.reason === 'leaving' ? `${first} is just about to head off.`
          : r?.reason === 'busy' ? `${first} can't talk right now${r.activity ? ` (${r.activity})` : ''}.`
            : `${first} can't talk right now.`;
      useWorldStore.getState().pushChat({ id: `sys:${g.a}`, handle: '·', text: why, ts: Date.now() });
      return;
    }
    // AS A SCENE (components/SceneChat): seated, facing their table, so the
    // camera comes in from across it — where the chair you would take stands.
    const height = (useWorldStore.getState().npcList || []).find((n) => Number(n?.avatarId) === g.a && n.rigged && !n.test)?.height || 0.75;
    openChat({
      avatarId: g.a, name: g.name, city: { mustGoAt: r.mustGoAt || null, nextActivity: r.nextActivity || null },
      scene: bodyScene(anchors.current.get(g.seat) || null, { avatarId: g.a, height, seated: true }),
    });
  };

  // Sit down across from one, and the conversation opens (once per sitting).
  const seated = useWorldStore((s) => (s.seated?.phase === 'seated' ? s.seated.target : null));
  useEffect(() => {
    if (!seated) { opened.current = null; return; }
    if (opened.current === seated) return;
    const t = tables.find((x) => (x.seats || []).includes(seated));
    const g = t && guests.find((x) => x.seat !== seated && (t.seats || []).includes(x.seat));
    if (!g) return;
    opened.current = seated;
    talkTo(g);
  }, [seated, guests]); // eslint-disable-line react-hooks/exhaustive-deps

  const toLocal = useMemo(() => (v) => { if (grp.current) { grp.current.updateMatrixWorld(true); grp.current.worldToLocal(v); } return v; }, []);
  return (
    <group ref={grp}>
      {model && guests.map((g) => (
        <Guest key={`${g.a}:${g.seat}`} g={g} model={model} toLocal={toLocal} onTap={() => talkTo(g)}
          anchorRef={(el) => { if (el) anchors.current.set(g.seat, el); else anchors.current.delete(g.seat); }} />
      ))}
    </group>
  );
}
